import { Injectable } from '@angular/core';
import { OrderListItem, OrderListItemWrapper } from '@jfw/types';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class WholesaleOrderListViewService {
  /// BehaviorSubject for OrderListWrapper
  private orderListWrapperSubject = new BehaviorSubject<OrderListItemWrapper>({
    orderList: [],
    status: 'PENDING',
  });

  // Observable for components to subscribe to
  orderListWrapper$: Observable<OrderListItemWrapper> =
    this.orderListWrapperSubject.asObservable();

  // EXT-6188: the default sort a producer wants for one specific order list,
  // keyed by that list's own array identity.
  //
  // This exists for the KMS/EZTux embed, where <ezt-pods> and
  // <ezt-order-list-view> are two separate custom elements wired together by
  // KMS-side page script we don't own -- so the pods component cannot bind
  // WholesaleOrderListViewComponent's defaultSortKey input the way
  // jfw-platform-portal and dealer-portal do. Both elements come from the one
  // createApplication() in portals/eztux/src/main.ts, so this singleton is the
  // only channel between them that lives in this repo.
  //
  // It is deliberately scoped to an array *instance* rather than just
  // remembering the last key set. order-entry (recentlyPlacedOrders),
  // event-planner (orders) and order-search all push their own lists into
  // WholesaleOrderListViewComponent via receivedOrderList without ever writing
  // this service, so a service-wide "last key wins" would leak a pod's
  // orderNumber sort onto those lists after a Data Pods visit in the same SPA
  // session. Matching on identity means a hint can only ever apply to the
  // exact list it was recorded for.
  private defaultSortKeyHint: {
    list: OrderListItem[];
    key: 'eventDate' | 'orderNumber';
  } | null = null;

  // Method to get the current value
  getOrderListWrapper(): OrderListItemWrapper {
    return this.orderListWrapperSubject.value;
  }

  // Method to update the value
  setOrderListWrapper(wrapper: OrderListItemWrapper): void {
    this.orderListWrapperSubject.next(wrapper);
  }

  /**
   * Records the default sort key a producer wants applied to `list`.
   * Only honored for that exact array -- see defaultSortKeyHint above.
   */
  setDefaultSortKeyForList(
    list: OrderListItem[],
    key: 'eventDate' | 'orderNumber',
  ): void {
    this.defaultSortKeyHint = { list, key };
  }

  /**
   * The default sort key recorded for `list`, or undefined if this list isn't
   * the one a hint was recorded against (so the caller falls back to its own
   * defaultSortKey input).
   */
  getDefaultSortKeyForList(
    list: OrderListItem[] | undefined,
  ): 'eventDate' | 'orderNumber' | undefined {
    if (!list || this.defaultSortKeyHint?.list !== list) {
      return undefined;
    }
    return this.defaultSortKeyHint.key;
  }
}
